/*
 * Copyright 2020 Thomer Gil [https://thomer.com/games/brickbreaker-js/]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// {{{ imports
import { GameScene } from './gamescene';
import { Brick } from './brick';
import { BB } from './globals';
// }}}

export class Wall {
    // {{{ members, constructors
    private _nbricks: number;
    private _scene: GameScene;
    private _bricks: Array<Brick>;

    constructor(scene: GameScene, level: number) {
        this._nbricks = 0;
        this._scene = scene;
        this._bricks = new Array<Brick>();

        //
        // draw a wall that alternates
        //
        // [....] [....] [....] [....]  <-- even rows
        // ..] [....] [....] [....] [.  <-- odd rows
        // [....] [....] [....] [....]
        // ..] [....] [....] [....] [.
        //
        // even rows have 1 more brick than odd rows, but the first and last brick
        // are only half bricks.
        //
        let y = BB.ABOVE_WALL_EMPTY_SPACE;
        for (let i = 0; i < BB.NROWS; i++) {
            let x = 0;
            for (let j = 0; j < BB.NBRICKS + 1; j++) {
                let brickWidth = BB.BRICK_WIDTH;

                // on odd rows, first and last brick is a half brick. because the
                // odd rows have one more BRICK_HSPACE than the even rows, subtract
                // half a BRICK_HSPACE from the width of the first and last brick.
                // (Otherwise the last brick would (invisibly) stick out to the right.
                if (i % 2 == 1 && (!j || j >= BB.NBRICKS)) {
                    brickWidth = brickWidth / 2 - BB.BRICK_HSPACE / 2;
                }

                // on even rows, skip the last brick, because it'll be drawn
                // outside the screen. phrased differently: the for loop goes
                // from j to bricksPerRow+1 -- the +1 is to accommodate bricks on
                // odd rows; for even rows, it's one too many.
                if (!(i % 2) && j >= BB.NBRICKS) {
                    continue;
                }

                const brick = new Brick(this, x + brickWidth / 2, y, brickWidth, BB.BRICK_HEIGHT, level);
                this._scene.add(brick);
                this._bricks.push(brick);
                this._nbricks += 1;

                x += brickWidth + BB.BRICK_HSPACE;
            }
            y += BB.BRICK_HEIGHT + BB.BRICK_VSPACE;
        }
    }
    // }}}

    // {{{ public
    // removes a brick from the wall
    public killBrick(brick: Brick): void {
        const i = this._bricks.findIndex((x) => x === brick);
        if (i > -1) {
            this._scene.remove(brick);
            this._bricks.splice(i, 1);
            this._nbricks -= 1;
        }
    }

    public bottomY(): number {
        return BB.ABOVE_WALL_EMPTY_SPACE + BB.NROWS * BB.BRICK_HEIGHT + (BB.NROWS - 1) * BB.BRICK_VSPACE;
    }

    public isEmpty(): boolean {
        return this._nbricks <= 0;
    }
    // }}}
}
